import React, { useState } from "react";
import { X } from "lucide-react";

const LABEL_CLASS = "text-[var(--input-label-color)] font-[var(--input-label-font-weight)]";
const LABEL_STYLE = { fontSize: "var(--input-label-font-size, 13px)" };

/*
 * Reason + remark popup shared by Cancel e-Invoice and Cancel e-Way Bill.
 * infoLines: [{ label, value }] shown at the top; note: muted line under them;
 * error: { message, details[] } from the last failed attempt.
 */
const CancelDocumentModal = ({
  title,
  infoLines = [],
  note,
  reasons = [],
  reasonsLoading = false,
  remarkMaxLength = 100,
  saving = false,
  error = null,
  onClose,
  onConfirm,
}) => {
  const [reason, setReason] = useState("");
  const [remark, setRemark] = useState("");

  const canSubmit = Boolean(reason) && Boolean(remark.trim()) && !saving;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}>
      <div className="bg-white rounded-[12px] shadow-xl w-full max-w-[460px] mx-4" style={{ fontFamily: "Inter, sans-serif" }}>
        {/* header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--light-border-color)]">
          <span className="font-[700] text-[var(--secondary)]" style={{ fontSize: "14px" }}>
            {title}
          </span>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-[var(--placeholder-color)] hover:text-[var(--secondary)]"
          >
            <X size={18} />
          </button>
        </div>

        {/* body */}
        <div className="px-5 py-5 flex flex-col gap-4">
          <div className="flex flex-col gap-1 text-[var(--secondary)]" style={{ fontSize: "13px" }}>
            {infoLines.map(({ label, value }) => (
              <div key={label} className="truncate" title={value ? String(value) : undefined}>
                <span className="font-[600]">{label}: </span>
                {value || "—"}
              </div>
            ))}
            {note && <div className="text-[var(--placeholder-color)]">{note}</div>}
          </div>

          {error && (
            <div className="rounded-[8px] border border-[#FECDCA] bg-[#FEF3F2] px-3 py-2 text-[#B42318]" style={{ fontSize: "12px" }}>
              <div className="font-[600]">{error.message}</div>
              {error.details?.length > 0 && (
                <ul className="list-disc pl-5">
                  {error.details.map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              )}
            </div>
          )}

          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS} style={LABEL_STYLE}>
              Cancellation Reason <span className="text-F51D00">*</span>
            </label>
            <select
              className="min-h-[38px] rounded-[8px] bg-white border border-[var(--input-border)] px-3
                text-[var(--input-color)] text-[length:var(--input-font-size)] outline-none
                focus:border-[var(--primary)]"
              value={reason}
              disabled={reasonsLoading}
              onChange={(e) => setReason(e.target.value)}
            >
              <option value="">{reasonsLoading ? "Loading reasons…" : "Select reason…"}</option>
              {reasons.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS} style={LABEL_STYLE}>
              Remarks <span className="text-F51D00">*</span>
            </label>
            <textarea
              rows={3}
              maxLength={remarkMaxLength}
              className="rounded-[8px] bg-white border border-[var(--input-border)] px-3 py-2
                text-[var(--input-color)] text-[length:var(--input-font-size)] outline-none
                focus:border-[var(--primary)] resize-none"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="Enter remark…"
            />
            <span className="self-end text-[var(--placeholder-color)]" style={{ fontSize: "11px" }}>
              {remark.length}/{remarkMaxLength}
            </span>
          </div>
        </div>

        {/* footer */}
        <div className="flex justify-end gap-3 px-5 py-4 border-t border-[var(--light-border-color)]">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md
              bg-[var(--draft-button-bg)] cursor-pointer
              text-[var(--draft-button-fg)]
              border border-[var(--draft-button-border)]
              text-[length:var(--draft-button-fs)]
              font-[var(--draft-button-fw)]"
          >
            Close
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => onConfirm({ reason, remark: remark.trim() })}
            className="flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md
              bg-F94B4B text-white border border-F94B4B
              font-[var(--primary-button-font-weight)]
              text-[length:var(--primary-button-create-fs)]
              disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Cancelling…" : "Confirm Cancel"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CancelDocumentModal;
